import { AppApiGatewayService, errorManager } from '.';
import { WebSocketRequest, WebSocketAction } from '../types';

const HEARTBEAT_INTERVAL = 30_000;
const HEARTBEAT_TIMEOUT = 60_000;

const MAX_RETRIES = 3;
const BASE_RETRY_DELAY = 1_000;
const MAX_RETRY_DELAY = 10_000;

enum WebSocketState {
  DISCONNECTED = "disconnected", // No active connection, ready to connect
  CONNECTING = "connecting",     // Connection attempt in progress
  CONNECTED = "connected",       // Active connection with heartbeat running
  RETRYING = "retrying"          // Waiting to retry after connection failure
}

enum HeartbeatType {
  PING = "ping",
  PONG = "pong"
}

class WebSocketManager {
  private static instance: WebSocketManager;
  private websocket: WebSocket | null = null;
  private state: WebSocketState = WebSocketState.DISCONNECTED;

  private service: AppApiGatewayService | null = null;
  private listeners: ((event: MessageEvent) => void)[] = [];
  private userId: string = "";
  private sessionId: string = "";

  private retryCount = 0;
  private retryTimer?: number;
  private heartbeatTimer?: number;

  private lastPong = Date.now();

  private constructor() {
    this.sessionId = crypto.randomUUID();
    this.attachSystemListeners();
  }

  static getInstance(): WebSocketManager {
    if (!WebSocketManager.instance) {
      WebSocketManager.instance = new WebSocketManager();
    }
    return WebSocketManager.instance;
  }

  async initialize(service: AppApiGatewayService, userId: string = ""): Promise<void> {
    // Skip if not disconnected
    if (this.state !== WebSocketState.DISCONNECTED) {
      console.log('Skipping initialize - not disconnected');
      return;
    }

    this.service = service;
    this.userId = userId || this.userId;

    try {
      const response = await service.getWebsocketPresignedUrl();

      if (response.statusCode === 200 && response.data?.websocket_presigned_url) {
        this.connect(response.data.websocket_presigned_url);
      } else {
        console.error('WebSocket initialization failed:', { statusCode: response.statusCode, hasUrl: !!response.data?.websocket_presigned_url });
        errorManager.showError('Unable to establish websocket connection. Please try again.');
        this.scheduleReconnect();
      }
    } catch (error) {
      console.error('Failed to initialize WebSocket:', error);
      errorManager.showError('Connection failed. Please check your internet connection.');
      this.scheduleReconnect();
    }
  }

  isConnected(): boolean {
    return this.websocket?.readyState === WebSocket.OPEN;
  }

  connect(url: string): void {
    if (this.state !== WebSocketState.DISCONNECTED) return;

    this.clearTimers();
    this.state = WebSocketState.CONNECTING;

    this.websocket = new WebSocket(url);
    this.websocket.onopen = () => this.handleOpen();
    this.websocket.onmessage = (event) => this.handleMessage(event);
    this.websocket.onerror = (event) => this.handleError(event);
    this.websocket.onclose = (event) => this.handleClose(event);
  }

  disconnect(): void {
    this.state = WebSocketState.DISCONNECTED;
    this.retryCount = 0;
    this.clearTimers();
    this.detachSystemListeners();
    this.websocket?.close(1000, "App unmounted");
    this.service = null;
    this.websocket = null;
  }

  private handleOpen(): void {
    console.log(`WebSocket connected`);
    this.state = WebSocketState.CONNECTED;
    this.retryCount = 0;
    this.lastPong = Date.now();
    
    // Send initial heartbeat immediately after connection
    const initialHeartbeat = { action: 'heartbeat', type: HeartbeatType.PING, userId: this.userId, timestamp: Date.now() };
    this.websocket!.send(JSON.stringify(initialHeartbeat));
    
    this.startHeartbeat();
    errorManager.clearError();
  }

  private handleClose(event: CloseEvent): void {
    console.log(`WebSocket closed`, { code: event.code, wasClean: event.wasClean });
    this.cleanupSocket();
    this.state = WebSocketState.DISCONNECTED;
    
    // Show appropriate error message based on close code
    if (!event.wasClean) {
      if (event.code === 1006) {
        errorManager.showError('Connection lost unexpectedly. Attempting to reconnect...');
      } else if (event.code >= 4000) {
        errorManager.showError('Server connection error. Please refresh the page.');
      } else {
        errorManager.showError('Connection interrupted. Reconnecting...');
      }
    }
    
    this.scheduleReconnect();
  }

  private handleError(event: Event): void {
    console.error(`WebSocket error:`, event);
    errorManager.showError('Network connection error. Please check your internet connection.');
  }
  
  private handleMessage(event: MessageEvent): void {
    
    try {
      const msg = JSON.parse(event.data);
      
      if (msg.type == HeartbeatType.PONG) {
        console.log('Heartbeat pong received');
        this.lastPong = Date.now();
        return;
      }
      this.listeners.forEach(listener => listener(event));
    } catch (error) {
      console.error('Failed to parse WebSocket message:', error, 'Raw data:', event.data);
    }
  }

  private startHeartbeat(): void {
    this.heartbeatTimer = window.setInterval(() => {
      if (this.state !== WebSocketState.CONNECTED || !this.websocket) return;
      if (this.websocket.readyState != WebSocket.OPEN) {
        console.warn("WebSocket → heartbeat detected non-open socket");
        this.websocket.close();
        return;
      }

      if (Date.now() - this.lastPong > HEARTBEAT_TIMEOUT) {
        console.warn("WebSocket → heartbeat timeout");
        errorManager.showError('Connection timeout. Reconnecting...');
        this.websocket.close();
        return;
      }
      
      const heartbeatMsg = { action: WebSocketAction.HEARTBEAT, type: HeartbeatType.PING, userId: this.userId, timestamp: Date.now() };
      console.log('WebSocket → sending heartbeat:', heartbeatMsg);
      this.websocket.send(JSON.stringify(heartbeatMsg));
    }, HEARTBEAT_INTERVAL);
  }

  private scheduleReconnect() {
    if (this.retryCount >= MAX_RETRIES) {
      this.state = WebSocketState.DISCONNECTED;
      console.warn("WS → retries exhausted, waiting for recovery signal");
      errorManager.showError('Unable to reconnect. Please refresh the page or check your connection.');
      return;
    }

    this.state = WebSocketState.RETRYING;

    const delay = Math.min(
      BASE_RETRY_DELAY * 2 ** this.retryCount,
      MAX_RETRY_DELAY
    );

    this.retryCount++;

    console.log(
      `WebSocket reconnecting in ${delay}ms (attempt ${this.retryCount})`
    );

    // Show retry attempt message
    errorManager.showError(`Reconnecting... (attempt ${this.retryCount}/${MAX_RETRIES})`);

    this.retryTimer = window.setTimeout(() => {
      this.state = WebSocketState.DISCONNECTED;
      this.reconnect();
    }, delay);
  }

  async reconnect(): Promise<void> {
    if (this.service) {
      await this.initialize(this.service);
    } else {
      console.error('Cannot reconnect - missing service');
    }
  }

  send(params: Omit<WebSocketRequest, 'userId' | 'sessionId'>): void {
    if (this.websocket?.readyState === WebSocket.OPEN) {
      const request: WebSocketRequest = {
        ...params,
        userId: this.userId,
        sessionId: this.sessionId
      };
      console.log('📤 WebSocket sending request:', request);
      this.websocket.send(JSON.stringify(request));
    }
  }

  addListener(callback: (event: MessageEvent) => void): void {
    this.listeners.push(callback);
  }

  removeListener(callback: (event: MessageEvent) => void): void {
    this.listeners = this.listeners.filter(listener => listener !== callback);
  }

  private cleanupSocket() {
    console.log('Cleaning up WebSocket');
    this.clearTimers();
    if (this.websocket) {
      this.websocket.onopen = null;
      this.websocket.onclose = null;
      this.websocket.onerror = null;
      this.websocket.onmessage = null!;
    }
    this.websocket = null;
  }

  private clearTimers() {
    clearTimeout(this.retryTimer);
    clearInterval(this.heartbeatTimer);
  }

  private attachSystemListeners() {
    window.addEventListener("online", this.handleRecoverySignal);
    document.addEventListener("visibilitychange", this.handleVisibilityChange);
    window.addEventListener("beforeunload", this.handleBeforeUnload);
  }

  private detachSystemListeners() {
    window.removeEventListener("online", this.handleRecoverySignal);
    document.removeEventListener("visibilitychange", this.handleVisibilityChange);
    window.removeEventListener("beforeunload", this.handleBeforeUnload);
  }

  private handleRecoverySignal = () => {
    if (this.state === WebSocketState.DISCONNECTED) {
      console.log("WS → network online, retrying");
      this.retryCount = 0;
      this.state = WebSocketState.DISCONNECTED;
      this.reconnect();
    }
  };

  private handleVisibilityChange = () => {
    if (
      document.visibilityState === "visible" &&
      this.state === WebSocketState.DISCONNECTED
    ) {
      console.log("WS → tab visible, retrying");
      this.retryCount = 0;
      this.state = WebSocketState.DISCONNECTED;
      this.reconnect();
    }
  };

  private handleBeforeUnload = () => {
    // Just close WebSocket cleanly - page is being destroyed anyway
    this.websocket?.close(1000, "Page unload");
  };
}

export default WebSocketManager.getInstance();